import { Box, Fade, Stack, styled } from '@mui/material'
import { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import {
  useActionState,
  useCurrentNetwork,
  useIsErrorChain,
} from 'store/app/hooks'
import { ReactComponent as IconArrow } from './assets/arrow.svg'
import { useOnClickOutside } from 'usehooks-ts'
import clsx from 'clsx'
import { useDepositToken, useTokenListOnDeposit } from 'store/deposit/hook'
import { useFetchEthereumBalance } from 'hooks/web3/useEthereumBalanceOf'
import { Currency } from 'api/v1/market'
import { useDispatch } from 'react-redux'
import { useEvmTokenBalance } from 'store/web3/hooks'
import { useEthereumTokenInfo } from 'hooks/web3/useEthereumTokenInfo'
import { toFixed } from 'utils/number'
import { formatUnits } from 'ethers/lib/utils'
import { updateDepositTokenAction } from 'store/deposit/actions'
import { atom, useAtom, useSetAtom } from 'jotai'
import Loading from 'components/Loading'
import { IconSelected } from 'components/Icons'

const DepositNetworksWrap = styled(Box)`
  display: flex;
  height: 56px;
  background-color: rgba(22, 25, 30, 1);
  border-radius: 6px;
  position: relative;
  z-index: 10;
  user-select: none;
`
const CurrentNetwork = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  cursor: pointer;

  .arrow {
    color: rgba(149, 164, 173, 1);
  }

  &.open {
    .arrow {
      transform: rotate(180deg);
      color: rgba(255, 255, 255, 1);
    }
  }
`
const NetworkIcon = styled('img')`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
`
const NetworkName = styled('div')`
  flex: 1;
  color: rgba(255, 255, 255, 1);
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
`
const NetworkPopup = styled(Stack)`
  position: absolute;
  top: 56px;
  left: 0;
  right: 0;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
  padding: 16px 0;
  background-color: rgba(42, 49, 57, 1);
  border-top: 1px solid rgba(160, 238, 9, 1);
  border-radius: 0 0 6px 6px;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.25);
`
const NetworkOption = styled('div')`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
  height: 48px;
  padding: 0 16px;
  transition: background-color 0.2s ease-in-out;

  .flag {
    display: none;
  }

  &:hover {
    background-color: rgba(57, 65, 73, 1);
    cursor: pointer;
  }

  &.checked {
    background-color: rgba(160, 238, 9, 0.1);
    cursor: default;

    > div {
      color: rgba(160, 238, 9, 1);
    }

    .flag {
      display: block;
    }
  }
`
const AssetsAvailable = styled('div')`
  color: rgba(255, 255, 255, 1);
  font-size: 14px;
  max-width: 200px;

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const popupAtom = atom<boolean>(false)

export const DepositCurrencies = memo(() => {
  const [showPopup, setShowPopup] = useAtom(popupAtom)
  const wrap = useRef<any>(null)
  const dispatch = useDispatch()

  useOnClickOutside(wrap, () => setShowPopup(false))

  const { layerOneChainId } = useCurrentNetwork()
  const supportTokens = useTokenListOnDeposit(layerOneChainId)
  const isErrorChain = useIsErrorChain()
  const selectedToken = useDepositToken()

  useFetchEthereumBalance(layerOneChainId)

  useEffect(() => {
    if (!selectedToken) {
      if (supportTokens?.length) {
        dispatch(updateDepositTokenAction(supportTokens[0]))
      }
    }
  }, [selectedToken, supportTokens])

  const { l2CurrencyId = 0 } = selectedToken ?? {}
  const balance = useEvmTokenBalance(layerOneChainId, l2CurrencyId)

  const { decimals = 0 } =
    useEthereumTokenInfo(layerOneChainId, l2CurrencyId) ?? {}

  const available = useMemo(() => {
    if (!balance) {
      return '0'
    }
    return toFixed(formatUnits(balance, decimals), 5)
  }, [balance, decimals])

  return (
    <DepositNetworksWrap ref={wrap}>
      <CurrentNetwork
        className={clsx({ open: showPopup })}
        onClick={() => setShowPopup(!showPopup)}
      >
        <NetworkIcon src={selectedToken?.tokenIconUrl} />
        <NetworkName>{selectedToken?.name}</NetworkName>
        <AssetsAvailable>Available: {available || '0'}</AssetsAvailable>
        <IconArrow className="arrow" />
      </CurrentNetwork>
      {showPopup ? (
        <NetworkPopup>
          {supportTokens.map((v) => (
            <CurrencyOption key={v.id} currency={v} />
          ))}
        </NetworkPopup>
      ) : null}
    </DepositNetworksWrap>
  )
})

const CurrencyOption = memo<{
  currency: Currency
}>(({ currency }) => {
  const { layerOneChainId } = useCurrentNetwork()
  const { id, l2CurrencyId, name, tokenIconUrl } = currency
  const dispatch = useDispatch()
  const selectedToken = useDepositToken()
  const balancesPulling = useActionState('ethereumBalancesPulling')
  const balance = useEvmTokenBalance(layerOneChainId, l2CurrencyId)
  const setPopup = useSetAtom(popupAtom)

  const { decimals = 0 } =
    useEthereumTokenInfo(layerOneChainId, l2CurrencyId) ?? {}

  const available = useMemo(() => {
    if (!balance) {
      return '0'
    }
    return toFixed(formatUnits(balance, decimals), 5)
  }, [balance, decimals])

  const onClickToken = useCallback(
    (selectedToken: Currency) => {
      dispatch(updateDepositTokenAction(selectedToken))
      setPopup(false)
    },
    [dispatch, setPopup],
  )

  return (
    <NetworkOption
      className={clsx({
        checked: selectedToken?.id === currency.id,
      })}
      onClick={() => onClickToken(currency)}
    >
      <NetworkIcon src={tokenIconUrl} />
      <NetworkName>{name}</NetworkName>
      {balancesPulling ? <Loading sx={{ flexShrink: 0 }} size={12} /> : null}
      <AssetsAvailable>
        <small>Available: {available}</small>
      </AssetsAvailable>
      <IconSelected className={'flag'} />
    </NetworkOption>
  )
})
